import { useEffect, useMemo, useRef, useState } from 'react';
import { noteName } from '../../core/decode';
import type { CcLane, DrumKind, Interpretation } from '../../core/types';
import { token, type ThemeId } from '../theme';

interface Props {
  song: Interpretation;
  position: number;
  playing: boolean;
  zoom: number;
  showRaw: boolean;
  audible: boolean[];
  theme: ThemeId;
  onSeek: (time: number) => void;
}

/** Left gutter: note-name column, then a narrow keyboard strip of KEYS_W px. */
const GUTTER = 58;
const KEYS_W = 22;
const RULER = 18;
const ROLL_H = 300;
const DRUM_ROW = 13;
const LANE_H = 46;
const DRUM_ORDER: DrumKind[] = ['openHat', 'closedHat', 'snare', 'tom', 'kick'];
const DRUM_LABEL: Record<DrumKind, string> = {
  openHat: 'OH',
  closedHat: 'CH',
  snare: 'Snare',
  tom: 'Tom',
  kick: 'Kick',
};
const MONO = '"Cascadia Mono", Consolas, ui-monospace, monospace';
const isBlack = (n: number) => [1, 3, 6, 8, 10].includes(n % 12);
const LANE_LABEL: Record<CcLane, string> = {
  cutoff: 'Filter cutoff',
  resonance: 'Resonance',
  pulseWidth: 'Pulse width',
  waveform: 'Waveform',
  volume: 'Volume',
};

interface LaneSeries {
  lane: CcLane;
  series: { channel: number; times: Float64Array; values: Uint8Array }[];
}

export function PianoRoll({ song, position, playing, zoom, showRaw, audible, theme, onSeek }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(800);
  const [scrollLeft, setScrollLeft] = useState(0);

  const melodic = useMemo(() => song.notes.filter((n) => !n.drum), [song]);
  const drums = useMemo(() => song.notes.filter((n) => n.drum), [song]);
  const range = useMemo(() => {
    let lo = 127;
    let hi = 0;
    for (const n of melodic) {
      lo = Math.min(lo, n.note);
      hi = Math.max(hi, n.note);
    }
    if (lo > hi) {
      lo = 48;
      hi = 72;
    }
    lo = Math.max(0, lo - 2);
    hi = Math.min(127, hi + 2);
    if (hi - lo < 24) {
      const mid = (hi + lo) / 2;
      lo = Math.max(0, Math.floor(mid - 12));
      hi = Math.min(127, lo + 24);
    }
    return { lo, hi };
  }, [melodic]);

  const lanes = useMemo<LaneSeries[]>(() => {
    const map = new Map<CcLane, Map<number, { t: number[]; v: number[] }>>();
    for (const c of song.ccs) {
      let byCh = map.get(c.lane);
      if (!byCh) map.set(c.lane, (byCh = new Map()));
      let s = byCh.get(c.channel);
      if (!s) byCh.set(c.channel, (s = { t: [], v: [] }));
      s.t.push(c.time);
      s.v.push(c.value);
    }
    const order: CcLane[] = ['cutoff', 'resonance', 'pulseWidth', 'waveform', 'volume'];
    return order
      .filter((l) => map.has(l))
      .map((lane) => ({
        lane,
        series: [...map.get(lane)!].map(([channel, s]) => ({
          channel,
          times: Float64Array.from(s.t),
          values: Uint8Array.from(s.v),
        })),
      }));
  }, [song]);

  const hasDrums = drums.length > 0;
  const drumH = hasDrums ? DRUM_ORDER.length * DRUM_ROW + 6 : 0;
  const height = RULER + ROLL_H + drumH + lanes.length * LANE_H;
  const contentWidth = GUTTER + song.duration * zoom + 40;

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Keep the playhead in view while playing.
  useEffect(() => {
    const el = scroller.current;
    if (!el || !playing) return;
    const x = GUTTER + position * zoom;
    if (x < el.scrollLeft + GUTTER || x > el.scrollLeft + el.clientWidth - 40) {
      el.scrollLeft = Math.max(0, x - GUTTER - 20);
    }
  }, [position, playing, zoom]);

  useEffect(() => {
    const cv = canvas.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.floor(width * dpr);
    cv.height = Math.floor(height * dpr);
    cv.style.width = `${width}px`;
    cv.style.height = `${height}px`;
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Colours come from the active theme's CSS tokens (src/index.css).
    const C = {
      bg: token('canvas-bg'),
      lane: token('panel'),
      grid: token('panel2'),
      bar: token('line'),
      text: token('muted'),
      key: token('panel2'),
      black: token('bg'),
      ink: token('text'),
    };
    const VOICE = [token('v1'), token('v2'), token('v3')];
    const DRUMS = token('drums');
    g.fillStyle = C.bg;
    g.fillRect(0, 0, width, height);

    const t0 = scrollLeft / zoom;
    const t1 = (scrollLeft + width) / zoom;
    const xOf = (t: number) => GUTTER + t * zoom - scrollLeft;
    const rows = range.hi - range.lo + 1;
    const rowH = ROLL_H / rows;
    const yOf = (note: number) => RULER + (range.hi - note) * rowH;

    // Black-key shading, plus a line under every C so octaves are easy to follow.
    for (let n = range.lo; n <= range.hi; n++) {
      if (isBlack(n)) {
        g.fillStyle = C.lane;
        g.fillRect(GUTTER, yOf(n), width, rowH);
      }
      if (n % 12 === 0) {
        g.fillStyle = C.bar;
        g.fillRect(GUTTER, Math.round(yOf(n) + rowH) - 1, width, 1);
      }
    }

    // Beat / bar grid.
    const beat = 60 / song.bpm;
    const firstBeat = Math.floor(t0 / beat);
    const beatPx = beat * zoom;
    for (let b = firstBeat; b * beat <= t1; b++) {
      const x = xOf(b * beat);
      const isBar = b % 4 === 0;
      if (!isBar && beatPx < 6) continue;
      g.fillStyle = isBar ? C.bar : C.grid;
      g.fillRect(Math.round(x), RULER, 1, height - RULER);
      if (isBar && (beatPx * 4 > 28 || (b / 4) % 4 === 0)) {
        g.fillStyle = C.text;
        g.font = '10px ui-sans-serif, system-ui';
        g.fillText(String(b / 4 + 1), x + 3, 12);
      }
    }

    // Notes.
    for (const n of melodic) {
      if (n.end < t0 || n.start > t1) continue;
      const tr = song.tracks[n.track];
      const x = xOf(n.start);
      const w = Math.max(2, (n.end - n.start) * zoom - 1);
      const y = yOf(n.note);
      g.globalAlpha = (audible[n.track] ? 1 : 0.18) * (0.45 + (0.55 * n.velocity) / 127);
      g.fillStyle = tr?.kind === 'drums' ? DRUMS : VOICE[n.voice];
      g.fillRect(x, y + 0.5, w, Math.max(1.5, rowH - 1));
      if (n.chord) {
        g.globalAlpha = 1;
        g.strokeStyle = C.ink;
        g.lineWidth = 0.75;
        g.strokeRect(x + 0.5, y + 1, w - 1, Math.max(1, rowH - 2));
      }
    }
    g.globalAlpha = 1;

    // Raw frequency trace.
    if (showRaw) {
      g.lineWidth = 1;
      song.rawPitch.forEach((trace, vi) => {
        g.strokeStyle = VOICE[vi];
        g.globalAlpha = 0.9;
        g.setLineDash([3, 2]);
        g.beginPath();
        let pen = false;
        const a = Math.max(0, Math.floor(t0 * song.tickRate) - 1);
        const b = Math.min(trace.length, Math.ceil(t1 * song.tickRate) + 1);
        for (let t = a; t < b; t++) {
          const p = trace[t];
          if (Number.isNaN(p) || p < range.lo - 1 || p > range.hi + 1) {
            pen = false;
            continue;
          }
          const x = xOf(t / song.tickRate);
          const y = yOf(p) + rowH / 2;
          if (pen) g.lineTo(x, y);
          else g.moveTo(x, y);
          pen = true;
        }
        g.stroke();
      });
      g.setLineDash([]);
      g.globalAlpha = 1;
    }

    // Drum strip.
    let y = RULER + ROLL_H;
    if (hasDrums) {
      g.fillStyle = C.grid;
      g.fillRect(0, y + 2, width, 1);
      for (const n of drums) {
        if (n.start > t1 || n.end < t0) continue;
        const row = DRUM_ORDER.indexOf(n.drum!);
        const x = xOf(n.start);
        g.globalAlpha = audible[n.track] ? 0.95 : 0.2;
        g.fillStyle = song.tracks[n.track]?.kind === 'drums' ? DRUMS : VOICE[n.voice];
        const cy = y + 6 + row * DRUM_ROW + DRUM_ROW / 2;
        g.beginPath();
        g.moveTo(x, cy - 4);
        g.lineTo(x + 4, cy);
        g.lineTo(x, cy + 4);
        g.lineTo(x - 4, cy);
        g.closePath();
        g.fill();
      }
      g.globalAlpha = 1;
      y += drumH;
    }

    // CC lanes.
    for (const { lane, series } of lanes) {
      g.fillStyle = C.lane;
      g.fillRect(GUTTER, y + 2, width, LANE_H - 4);
      for (const s of series) {
        g.strokeStyle = s.channel === 9 ? DRUMS : (VOICE[s.channel] ?? C.ink);
        g.lineWidth = 1.25;
        g.beginPath();
        const vy = (v: number) => y + LANE_H - 4 - (v / 127) * (LANE_H - 8);
        let lo = 0;
        let hi = s.times.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (s.times[mid] < t0) lo = mid + 1;
          else hi = mid;
        }
        let i = Math.max(0, lo - 1);
        let px = Math.max(GUTTER, xOf(s.times[i]));
        g.moveTo(px, vy(s.values[i]));
        for (i = i + 1; i < s.times.length && s.times[i - 1] <= t1; i++) {
          const x = xOf(s.times[i]);
          g.lineTo(x, vy(s.values[i - 1]));
          g.lineTo(x, vy(s.values[i]));
          px = x;
        }
        g.lineTo(Math.min(width, xOf(song.duration)), vy(s.values[Math.min(i, s.values.length) - 1]));
        g.stroke();
      }
      g.fillStyle = C.text;
      g.font = '10px ui-sans-serif, system-ui';
      g.fillText(LANE_LABEL[lane], GUTTER + 4, y + 12);
      y += LANE_H;
    }

    // Playhead.
    const px = xOf(position);
    if (px >= GUTTER && px <= width) {
      g.fillStyle = C.ink;
      g.fillRect(Math.round(px), RULER, 1.5, height - RULER);
    }

    // Keyboard gutter (drawn last so it stays fixed): note names in their own
    // column on the left, never covered by keys; a narrow keyboard on the right.
    const keysX = GUTTER - KEYS_W;
    g.fillStyle = C.lane;
    g.fillRect(0, 0, GUTTER, height);
    g.fillStyle = C.key;
    g.fillRect(keysX, RULER, KEYS_W, ROLL_H);
    g.textAlign = 'right';
    g.textBaseline = 'middle';
    g.font = `10px ${MONO}`;
    for (let n = range.lo; n <= range.hi; n++) {
      const y = yOf(n);
      if (isBlack(n)) {
        g.fillStyle = C.black;
        g.fillRect(keysX, y, KEYS_W * 0.62, rowH);
      }
      if (n % 12 === 0) {
        g.fillStyle = C.bar;
        g.fillRect(keysX, Math.round(y + rowH) - 1, KEYS_W, 1);
        g.fillStyle = C.ink;
        g.fillText(noteName(n), keysX - 5, y + rowH / 2);
      }
    }
    if (hasDrums) {
      g.font = `9px ${MONO}`;
      g.fillStyle = C.text;
      DRUM_ORDER.forEach((kind, row) => {
        g.fillText(DRUM_LABEL[kind], GUTTER - 5, RULER + ROLL_H + 6 + row * DRUM_ROW + DRUM_ROW / 2);
      });
    }
    g.textAlign = 'left';
    g.textBaseline = 'alphabetic';
    g.fillStyle = C.grid;
    g.fillRect(GUTTER - 1, 0, 1, height);
    g.fillRect(0, RULER - 1, width, 1);
  }, [
    song,
    width,
    height,
    scrollLeft,
    zoom,
    position,
    showRaw,
    audible,
    theme,
    melodic,
    drums,
    range,
    lanes,
    hasDrums,
    drumH,
  ]);

  const describe = `${song.notes.length} notes on ${song.tracks.length} tracks, ${song.duration.toFixed(1)} seconds at ${song.bpm.toFixed(1)} BPM`;

  return (
    <div
      ref={scroller}
      className="relative overflow-x-auto overflow-y-hidden rounded-md border border-line"
      onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)}
    >
      <div style={{ width: contentWidth, height }} className="relative">
        <canvas
          ref={canvas}
          role="img"
          aria-label={`Piano roll: ${describe}`}
          className="sticky left-0 top-0 block cursor-crosshair"
          onMouseDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            if (x < GUTTER) return;
            onSeek(Math.max(0, Math.min(song.duration, (x - GUTTER + scrollLeft) / zoom)));
          }}
        />
      </div>
    </div>
  );
}
